/**
 * NOS-125: Media series CRUD with cover image.
 * NOS-262: config over the shared taxonomy scaffold.
 */
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  MediaImageUploadField,
  MediaTaxonomyPage,
  type TaxonomyColumn,
} from "@/components/dashboard/MediaTaxonomyPage"
import {
  useMediaSeries,
  type MediaSeriesInput,
  type MediaSeriesWithCount,
} from "@/hooks/useMediaSeries"
import { generateSlug } from "@/lib/slugUtils"

type SeriesForm = {
  title: string
  slug: string
  description: string
  cover_image: string | null
}

const columns: TaxonomyColumn<MediaSeriesWithCount>[] = [
  {
    headClassName: "w-[88px]",
    cell: (s) =>
      s.cover_image ? (
        <img src={s.cover_image} alt="" className="h-10 w-16 rounded object-cover" />
      ) : (
        <div className="h-10 w-16 rounded bg-muted" />
      ),
  },
  { header: "Title", cell: (s) => s.title, cellClassName: "font-medium" },
  { header: "Slug", cell: (s) => s.slug, cellClassName: "text-muted-foreground" },
  { header: "Videos", cell: (s) => s.video_count },
]

export function MediaSeriesPage() {
  return (
    <MediaTaxonomyPage<MediaSeriesWithCount, SeriesForm, MediaSeriesInput>
      useTaxonomy={useMediaSeries}
      title="Series"
      tagline="Group sermons (e.g. by season)."
      entity="Series"
      columns={columns}
      dialogClassName="max-w-lg"
      emptyForm={{ title: "", slug: "", description: "", cover_image: null }}
      formFromRow={(s) => ({
        title: s.title,
        slug: s.slug,
        description: s.description ?? "",
        cover_image: s.cover_image,
      })}
      validate={(f) => (f.title.trim() ? null : "Title is required")}
      toPayload={(f) => ({
        title: f.title,
        slug: f.slug,
        description: f.description,
        cover_image: f.cover_image,
      })}
      deleteWarning="Remove all videos from this series before deleting."
      renderFields={(form, setForm, editing) => (
        <>
          <div>
            <Label>Title</Label>
            <Input
              value={form.title}
              onChange={(e) => {
                const title = e.target.value
                setForm((f) => ({
                  ...f,
                  title,
                  // Slug follows the title until it has been edited by hand.
                  slug:
                    !editing || f.slug === generateSlug(editing.title)
                      ? generateSlug(title)
                      : f.slug,
                }))
              }}
            />
          </div>
          <div>
            <Label>Slug</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={3}
            />
          </div>
          <div>
            <Label>Cover (16:9 recommended)</Label>
            <MediaImageUploadField
              folder="series-covers"
              successMessage="Cover uploaded"
              onUploaded={(url) => setForm((f) => ({ ...f, cover_image: url }))}
            />
          </div>
        </>
      )}
    />
  )
}
